import React, { useContext, useEffect, useRef, useState } from 'react';
import { FormControlLabel, Switch, Box, IconButton, Button, Tooltip } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { AddOutlined, EditOutlined, VisibilityOutlined } from '@mui/icons-material';
import { gql } from '@apollo/client';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import Table from 'components/Table';
import TableHeader from 'components/Table/TableHeader';
import TableSearch from 'components/Table/TableSearch';
import { GET_LENDER_PROFILES_WITH_OCA_ACCESS } from 'queries/common';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import { CommonDataContext } from 'contexts/CommonDataContext';
import { createDate, programStatus } from 'services/proposalsService';
import { useModal } from '../../hooks/useModal';
import { ModalsKeys } from '../../global';
import {
  FinanceProgramModal,
  PreviewFinanceProgramModal,
  mapCriteriaFormValuesToCreateInput,
  mapFinanceProgramEntityToFormValues,
  mapFormValuesToCreateFinanceProgramInput,
  mapFormValuesToUpdateFinanceProgramInput,
} from '../../modules/financeProgram';
import { useNotifications } from '../../modules/notification';
import { ModalsContainer } from '../../components/shared/Modals';
import { InsuranceCoverageCell } from './InsuranceCoverageCell';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import { keyBy } from 'utils/arrayUtils';
import { PageContainer } from '../../components/shared/Page/PageContainer';
import { CardContainer } from '../../components/shared/CardContainer/CardContainer';

const GET_VP = gql`
  query {
    vendorProfile {
      territoryManagement
      lenderProfiles {
        id
        lenderName
        lenderProfileId
        lenderChannelCode
        lenderTypeCode
        dealerRelationship
        lenderLogo
        isDisabled
      }
    }
  }
`;

const GET_FINANCE_PROGRAMS = gql`
  query GetFinancePrograms($accountId: ID!, $lenderProfileIds: [ID]) {
    financePrograms(accountId: $accountId, lenderProfileIds: $lenderProfileIds) {
      accountId
      financeProgramId
      nameInternal
      dealerTier
      paymentOptionsConfiguration
      validDateEnd
      validDateStart
      createdDateTime
      modifiedDateTime
      insuranceCoverage {
        coverageRequirements
        certificateRequirements
        otherInstructions
      }
      financeProgramToLenderProfiles {
        financeProgramToLenderProfileId
        lenderProfileId
        isDisabled
      }
      prescreenCriteria {
        prescreenCriteriaId
        accountId
        active
        jsonCriteria
        name
        guidelines
      }
      financeProgramModificationSettings {
        markup
      }
      submissionSettings {
        excludedDocuments
        autoSubmitUserProfileId
      }
      scoreTiers {
        tiers
      }
    }
  }
`;

const QUERY_VENDOR_PROFILES_ASSOCIATED_TO_LENDER = gql`
  query vendorProfilesByDynamicsLenderAccountId($dynamicsAccountId: ID!) {
    vendorProfilesByDynamicsLenderAccountId(dynamicsAccountId: $dynamicsAccountId) {
      id
      dynamicsId
      name
      accountId
      dynamicsAccountId
      vpStatus
      lenderProfiles {
        lenderProfileId
        isDisabled
        dealerTier
      }
    }
  }
`;

export const Q_Prescreen_Criteria = gql`
  query PreScreenCriteria($accountId: ID!) {
    prescreenCriteria(accountId: $accountId) {
      prescreenCriteriaId
      name
      accountId
      userProfileIdCreatedBy
      jsonCriteria
      active
      notes
      guidelines
      userProfileIdCreatedBy
      createdDateTime
    }
  }
`;

const M_Create_Prescreen_Criteria = gql`
  mutation CreatePrescreenCriteria($input: CreatePrescreenCriteriaInput!) {
    createPrescreenCriteria(input: $input) {
      prescreenCriteriaId
      name
      accountId
      userProfileIdCreatedBy
      jsonCriteria
      active
      notes
      guidelines
    }
  }
`;

const CREATE_FINANCE_PROGRAM = gql`
  mutation CreateFinanceProgram($input: CreateFinanceProgramInput!) {
    createFinanceProgram(input: $input) {
      accountId
      financeProgramId
      nameInternal
      dealerTier
      paymentOptionsConfiguration
      validDateEnd
      validDateStart
      createdDateTime
      modifiedDateTime
      insuranceCoverage {
        coverageRequirements
        certificateRequirements
        otherInstructions
      }
      financeProgramToLenderProfiles {
        financeProgramToLenderProfileId
        lenderProfileId
        createdDateTime
        modifiedDateTime
        isDisabled
      }
      prescreenCriteria {
        prescreenCriteriaId
        accountId
        active
        jsonCriteria
        name
        guidelines
      }
      financeProgramModificationSettings {
        markup
      }
      scoreTiers {
        tiers
      }
    }
  }
`;

const UPDATE_FINANCE_PROGRAM = gql`
  mutation UpdateFinanceProgram($input: UpdateFinanceProgramInput!) {
    updateFinanceProgram(input: $input) {
      accountId
      financeProgramId
      nameInternal
      dealerTier
      paymentOptionsConfiguration
      validDateEnd
      validDateStart
      createdDateTime
      modifiedDateTime
      insuranceCoverage {
        coverageRequirements
        certificateRequirements
        otherInstructions
      }
      financeProgramToLenderProfiles {
        financeProgramToLenderProfileId
        lenderProfileId
        createdDateTime
        modifiedDateTime
        isDisabled
      }
      prescreenCriteria {
        prescreenCriteriaId
        accountId
        active
        jsonCriteria
        name
        guidelines
      }
      financeProgramModificationSettings {
        markup
      }
      scoreTiers {
        tiers
      }
    }
  }
`;

const UPDATE_FINANCE_PROGRAM_TO_LENDER_PROFILE = gql`
  mutation UpdateFinanceProgramToLenderProfile($input: UpdateFinanceProgramToLenderProfileInput!) {
    updateFinanceProgramToLenderProfile(input: $input) {
      lenderProfileId
      financeProgramToLenderProfileId
      createdDateTime
      modifiedDateTime
      isDisabled
    }
  }
`;

const statusTitles = {
  Active: 'Finance program is availabled for you to use.',
  Expired: 'Finance program has expired and is no longer available to use.',
  Prepared: 'Finance program is ready to be used but not available yet.',
};

const FinanceProgramMgmt = ({ targetLenderProfileId }) => {
  const classes = useStyles();

  const { showNotification } = useNotifications();

  const userProfile = useSelector(state => state.userProfile);
  const account = useSelector(state => state.account);
  const { isLenderUser, isDealerUser } = useContext(CommonDataContext);
  const searchRef = useRef({ prevSearch: undefined, currentSearch: '' });
  const [lenderProfiles, setLenderProfiles] = useState([]);
  const [pageNumber, setPageNumber] = useState(0);
  const [pageSizeValue, setPageSizeValue] = useState(5);
  const [savedListSettings, setSavedListSettings] = useState({ page: 0, pageSize: 20 });
  const [dealerVendorProfiles, setDealerVendorProfiles] = useState(null);
  const [criteriaOptions, setCriteriaOptions] = useState([]);
  const [financeProgramsToShow, setFinanceProgramsToShow] = useState([]);
  const [isNeedToShowDisabled, setIsNeedToShowDisabled] = useState(false);

  const targetLender = lenderProfiles.find(lenderProfile => {
    return lenderProfile.id === targetLenderProfileId;
  });

  const {
    error,
    data: financeProgramsData,
    loading: fpLoading,
    refetch: fetchFinancePrograms,
  } = useQuery(GET_FINANCE_PROGRAMS, {
    fetchPolicy: 'no-cache',
    context: { authRequired: true },
    variables: {
      accountId: account.id,
      lenderProfileIds: targetLenderProfileId ? [targetLenderProfileId] : [],
    },
  });

  const { data: lenderProfilesWithOcaAccessData } = useQuery(GET_LENDER_PROFILES_WITH_OCA_ACCESS, {
    variables: { accountId: account.id },
  });

  const [createLenderProgram] = useMutation(CREATE_FINANCE_PROGRAM, {
    context: { authRequired: true },
  });

  const [updateLenderProgram] = useMutation(UPDATE_FINANCE_PROGRAM, {
    context: { authRequired: true },
  });

  const [updateFinanceProgramToLenderProfile, { loading: isUpdateFinanceProgramToLenderProfileLoading }] = useMutation(
    UPDATE_FINANCE_PROGRAM_TO_LENDER_PROFILE,
    {
      context: { authRequired: true },
    }
  );

  const [createPrescreenCriteria] = useMutation(M_Create_Prescreen_Criteria, {
    context: { authRequired: true },
  });

  const { loading: lenderProfilesLoading } = useQuery(GET_VP, {
    fetchPolicy: 'no-cache',
    context: { authRequired: true },
    onCompleted: data => {
      let lenderProfiles = data.vendorProfile?.lenderProfiles;
      if (lenderProfiles) {
        setLenderProfiles(lenderProfiles.filter(profile => profile.id));
      }
    },
  });

  useQuery(Q_Prescreen_Criteria, {
    fetchPolicy: 'no-cache',
    context: { authRequired: true },
    variables: { accountId: account.id },
    onCompleted(data) {
      const setOfCriteria = data?.prescreenCriteria ?? [];

      const options = setOfCriteria.map(criteria => ({
        label: criteria.name,
        value: criteria.prescreenCriteriaId,
        createdDateTime: criteria?.createdDateTime,
        formRules: criteria?.jsonCriteria?.formRules ?? [],
      }));

      setCriteriaOptions(options);
    },
  });

  const [fetchDealerVPs, { loading: dealerVendorProfilesLoading }] = useLazyQuery(
    QUERY_VENDOR_PROFILES_ASSOCIATED_TO_LENDER,
    {
      context: { authRequired: true },
      fetchPolicy: 'no-cache',
      onCompleted: data => {
        if (data.vendorProfilesByDynamicsLenderAccountId)
          setDealerVendorProfiles(data.vendorProfilesByDynamicsLenderAccountId);
      },
    }
  );

  useEffect(() => {
    if (!financeProgramsData?.financePrograms?.length) {
      return;
    }

    const financeProgramsTableData = financeProgramsData.financePrograms.map(fp => ({
      ...fp,
      financeProgramToLenderProfile: fp.financeProgramToLenderProfiles?.find(fptlp =>
        lenderProfiles.some(lp => lp.id === fptlp.lenderProfileId)
      ),
    }));

    const filteredByDisabled = isNeedToShowDisabled
      ? financeProgramsTableData
      : financeProgramsTableData.filter(fptd => !fptd.financeProgramToLenderProfile?.isDisabled);
    setFinanceProgramsToShow(filteredByDisabled?.sort((a, b) => a?.nameInternal?.localeCompare(b?.nameInternal)) ?? []);
  }, [financeProgramsData, isNeedToShowDisabled, lenderProfiles]);

  const { handleOpen, handleClose } = useModal(ModalsKeys.FinanceForm);
  const { handleOpen: handleOpenPreviewDialog, handleClose: handleClosePreviewDialog } = useModal(
    ModalsKeys.PreviewFinanceForm
  );

  const lpsWithOcaAccessByLenderProfileId = keyBy(
    lenderProfilesWithOcaAccessData?.lenderProfilesWithOcaAccess?.data,
    'lenderProfileId'
  );

  const handleDisable = React.useCallback(
    async financeProgramToLenderProfile => {
      try {
        await updateFinanceProgramToLenderProfile({
          variables: {
            input: {
              financeProgramToLenderProfileId: financeProgramToLenderProfile.financeProgramToLenderProfileId,
              isDisabled: !financeProgramToLenderProfile.isDisabled,
            },
          },
        });
        handleClosePreviewDialog();
        showNotification(`Saved!`);
        await fetchFinancePrograms();
      } catch (error) {
        showNotification(`Error!`, {
          type: 'error',
        });
      }
    },
    [updateFinanceProgramToLenderProfile, handleClosePreviewDialog, showNotification, fetchFinancePrograms]
  );

  const handleCreateFinanceProgram = () => {
    handleOpen({
      criteriaOptions,
      isMultipleLenders: true,
      lendersOptions: lenderProfiles,
      userType: isLenderUser ? 'lender' : 'dealer',
      dealerVendorProfileOptions: dealerVendorProfiles,
      isLendersSelectDisabled: !!targetLenderProfileId,
      targetLenders: targetLenderProfileId ? [targetLenderProfileId] : null,
      lpsWithOcaAccessByLenderProfileId,
      onSubmit: async formValue => {
        try {
          const preparedData = mapFormValuesToCreateFinanceProgramInput(formValue, account.id);

          const { data: createdCriteria } = await createPrescreenCriteria({
            variables: {
              input: preparedData.prescreenCriteriaInput,
            },
          });

          const { data: createdProgram } = await createLenderProgram({
            variables: {
              input: {
                ...preparedData,
                prescreenCriteriaIdToLink: createdCriteria?.createPrescreenCriteria?.prescreenCriteriaId,
              },
            },
          });

          await updateLenderProgram({
            variables: {
              input: {
                financeProgramId: createdProgram?.createFinanceProgram?.financeProgramId,
                prescreenCriteriaIdToLink: createdCriteria?.createPrescreenCriteria?.prescreenCriteriaId,
              },
            },
          });

          showNotification(`Finance program created!`);

          handleClose();
        } catch (error) {
          console.log(error);
          showNotification(`Finance program was not created!`, {
            type: 'error',
          });
        }
      },
    });
  };

  const handleUpdateFinanceProgram = React.useCallback(
    financeProgramForUpdate => {
      const programFormValue = mapFinanceProgramEntityToFormValues(
        financeProgramForUpdate,
        lenderProfiles,
        dealerVendorProfiles
      );
      const { financeProgramToLenderProfile } = financeProgramForUpdate;

      handleOpenPreviewDialog({
        program: programFormValue,
        lpsWithOcaAccessByLenderProfileId,
        onEdit: () => {
          handleOpen({
            criteriaOptions,
            isMultipleLenders: true,
            program: programFormValue,
            lendersOptions: lenderProfiles,
            userType: isLenderUser ? 'lender' : 'dealer',
            dealerVendorProfileOptions: dealerVendorProfiles,
            isLendersSelectDisabled: !!targetLenderProfileId,
            targetLenders: targetLenderProfileId ? [targetLenderProfileId] : null,
            lpsWithOcaAccessByLenderProfileId,
            onSubmit: async formValue => {
              try {
                const preparedData = mapFormValuesToUpdateFinanceProgramInput(financeProgramForUpdate, formValue);

                await updateLenderProgram({
                  variables: {
                    input: preparedData,
                  },
                });

                const isCriteriaDidntUpdate =
                  financeProgramForUpdate?.prescreenCriteria?.prescreenCriteriaId ===
                  formValue?.program?.third?.criteria?.value;

                if (!isCriteriaDidntUpdate) {
                  const { data: createdCriteria } = await createPrescreenCriteria({
                    variables: {
                      input: mapCriteriaFormValuesToCreateInput(formValue, account.id),
                    },
                  });

                  await updateLenderProgram({
                    variables: {
                      input: {
                        financeProgramId: formValue.program.id,
                        prescreenCriteriaIdToLink: createdCriteria?.createPrescreenCriteria?.prescreenCriteriaId,
                      },
                    },
                  });
                }

                await fetchFinancePrograms();

                showNotification(`Finance program updated!`);

                handleClose();
              } catch (error) {
                showNotification(`Finance program was not updated!`, {
                  type: 'error',
                });
              }
            },
          });
        },
        disableButton: isDealerUser && financeProgramToLenderProfile && (
          <Button
            onClick={() => handleDisable(financeProgramToLenderProfile)}
            loading={isUpdateFinanceProgramToLenderProfileLoading}
            disabled={targetLender?.isDisabled}
            color={financeProgramToLenderProfile.isDisabled ? 'primary' : 'error'}
            sx={{ marginRight: 'auto' }}
          >
            {financeProgramToLenderProfile.isDisabled ? 'Enable Program' : 'Disable Program'}
          </Button>
        ),
      });
    },
    [
      lenderProfiles,
      dealerVendorProfiles,
      handleOpenPreviewDialog,
      lpsWithOcaAccessByLenderProfileId,
      criteriaOptions,
      isLenderUser,
      targetLenderProfileId,
      handleOpen,
      updateLenderProgram,
      createPrescreenCriteria,
      account.id,
      fetchFinancePrograms,
      showNotification,
      handleClose,
      isDealerUser,
      isUpdateFinanceProgramToLenderProfileLoading,
      targetLender,
      handleDisable,
    ]
  );

  const getActionsCell = React.useCallback(
    props => {
      return (
        <Box display="flex">
          <IconButton
            style={{ padding: 4, marginLeft: 6 }}
            onClick={() => {
              handleUpdateFinanceProgram(props.original);
            }}
            color="primary"
            aria-label="change contact"
            component="button"
            size="large"
          >
            {props.value === account.id || isLenderUser ? (
              <>
                <EditOutlined style={{ fontSize: 24 }} />
                <span style={{ fontSize: 12, marginLeft: 4 }}>Edit</span>
              </>
            ) : (
              <>
                <VisibilityOutlined style={{ fontSize: 24 }} />
                <span style={{ fontSize: 12, marginLeft: 4 }}>View</span>
              </>
            )}
          </IconButton>
        </Box>
      );
    },
    [account.id, isLenderUser, handleUpdateFinanceProgram]
  );

  const findLender = React.useCallback(
    props => {
      return lenderProfiles?.find(lp => lp.id === _.get(props, 'value.lenderProfileId'));
    },
    [lenderProfiles]
  );

  const handleIsNeedToShowDisabledChange = event => {
    setIsNeedToShowDisabled(event.target.checked);
  };

  const columns = React.useMemo(() => {
    const baseColumns = [
      {
        Header: 'Name',
        accessor: 'nameInternal',
      },
      {
        Header: 'Status',
        Cell: ({ original }) => {
          const status = programStatus(original);

          return (
            <Box display="flex">
              <div title={statusTitles[status]} className={classes[`program${status}`]}>
                {status}
              </div>
              {original.financeProgramToLenderProfile?.isDisabled && (
                <Tooltip title="Finance program disabled" placement="top">
                  <ReportProblemOutlinedIcon sx={{ color: '#ec9c12', ml: 1 }} />
                </Tooltip>
              )}
            </Box>
          );
        },
      },
      {
        Header: 'Created',
        accessor: 'createdDateTime',
        Cell: ({ value }) => createDate(value).toLocaleDateString(),
      },
      {
        Header: 'Modified',
        accessor: 'modifiedDateTime',
        Cell: ({ value }) => createDate(value).toLocaleDateString(),
      },
      {
        Header: 'Insurance Coverage',
        accessor: 'insuranceCoverage',
        Cell: ({ value }) => <InsuranceCoverageCell insuranceCoverage={value} />,
      },
      {
        Header: 'Owned By',
        accessor: 'accountId',
        Cell: props => {
          if (props.value === account.id) {
            return 'You';
          } else if (isLenderUser) {
            return 'Dealer';
          } else {
            return 'Lender';
          }
        },
      },
      {
        Header: 'Actions',
        id: 'edit',
        accessor: 'accountId',
        Cell: getActionsCell,
      },
    ];

    if (isLenderUser) {
      baseColumns.splice(1, 0, {
        Header: 'Dealers (# of)',
        accessor: 'financeProgramToLenderProfiles',
        Cell: props => {
          return (
            <div style={{ width: '100%', textAlign: 'center' }}>
              {props.value && dealerVendorProfiles
                ? [...dealerVendorProfiles]?.filter(vp =>
                    props.value.map(lp => lp.lenderProfileId)?.includes(_.get(vp, 'lenderProfiles[0].lenderProfileId'))
                  )?.length
                : ''}
            </div>
          );
        },
      });
    } else {
      baseColumns.splice(1, 0, {
        Header: 'Lender',
        accessor: 'financeProgramToLenderProfile',
        Cell: props => {
          const lender = findLender(props);

          return <>{lender?.lenderName}</>;
        },
      });
    }

    return baseColumns;
  }, [dealerVendorProfiles, isLenderUser, account.id, classes, findLender, getActionsCell]);

  useEffect(() => {
    if (isLenderUser) {
      fetchDealerVPs({
        variables: {
          dynamicsAccountId: account?.dynamicsAccountId,
        },
      });
    }
  }, [account, isLenderUser, fetchDealerVPs]);

  const modalsWithKeys = {
    [ModalsKeys.FinanceForm]: <FinanceProgramModal />,
    [ModalsKeys.PreviewFinanceForm]: <PreviewFinanceProgramModal />,
  };

  return (
    <PageContainer
      loading={fpLoading || (isDealerUser && lenderProfilesLoading) || (isLenderUser && dealerVendorProfilesLoading)}
      error={error}
    >
      <CardContainer>
        <TableHeader
          filterComps={
            <TableSearch
              setPageNumber={setPageNumber}
              setSavedListSettings={setSavedListSettings}
              searchRef={searchRef}
              savedListSettings={savedListSettings}
            />
          }
        >
          {isDealerUser && (
            <FormControlLabel
              sx={{ minWidth: '170px' }}
              control={
                <Switch checked={isNeedToShowDisabled} onChange={handleIsNeedToShowDisabledChange} color="primary" />
              }
              label="Show Disabled"
            />
          )}

          {!_.includes(['creditMgr'], userProfile.vendorContactRole) && (
            <Button
              color="primary"
              sx={{ minWidth: '150px' }}
              size="small"
              data-tour="add-user-button"
              onClick={handleCreateFinanceProgram}
            >
              <AddOutlined />
              Add Program
            </Button>
          )}
        </TableHeader>

        <Table
          color="primary"
          data={financeProgramsToShow}
          columns={columns}
          pageSize={pageSizeValue}
          pageNumber={pageNumber}
          defaultPageSize={savedListSettings.pageSize}
          showPaginationTop={false}
          showPaginationBottom={true}
          onPageChange={changePage => {
            setPageNumber(changePage);
          }}
          onPageSizeChange={changePageSize => {
            setPageSizeValue(changePageSize);
            setPageNumber(0);
          }}
          className={classes.root + ' -highlight'}
          NoDataComponent={() => null}
        />
      </CardContainer>

      <ModalsContainer modalsWithKeys={modalsWithKeys} />
    </PageContainer>
  );
};

const useStyles = makeStyles({
  root: {
    '& .ReactTable .access-control-locations-cell': {
      overflow: 'visible',
    },
    '& .ReactTable .rt-table': {
      overflow: 'visible',
    },
    '& .ReactTable .rt-tbody': {
      overflow: 'visible',
    },

    '& .access-control-locations-cell .tag-list .tag-item': {
      display: 'block',
    },
    '& .access-control-locations-cell .tag-list .tag-item span': {
      color: 'black',
      fontSize: '14px',
    },

    '& .access-control-locations-cell .tag-list .tag-item:nth-child(2)': {
      position: 'initial',
    },
  },
  right: {
    textAlign: 'right',
  },
});

export default FinanceProgramMgmt;
